import { DarwinModal } from 'components/darwin-ui/Modal';
import DarwinTypography from 'components/darwin-ui/Typography';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useUI } from 'contexts/UiContext';
import { useTranslation } from 'react-i18next';
import styles from './index.module.css';
import { ErrorType, InputType } from 'types/genericTypes';
import { GenericInput, GenericInputProps } from 'components/ui/GenericInput';
import {
  CONTACT_EMAIL,
  CONTACT_NAME,
  EMAIL_REGEX_VALIDATION,
  NAME,
  URL_REGEX_VALIDATION,
} from 'utils/dataSources';
import { DarwinCol, DarwinFlex } from 'components/darwin-ui';
import { Form } from 'antd';
import { useForm, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import {
  useCreateDataSource,
  useUpdateDataSourceByID,
} from 'api/services/dataSourceService';
import { DataSourcesListView } from 'types/dataSourcesTypes';

interface CreateEditOrViewDataSourceModalProps {
  mode: 'creating' | 'editing' | 'viewing';
  dataSourceData?: DataSourcesListView;
  onCancel: () => void;
  open: boolean;
}

export type FormFields = {
  name: string;
  contactName: string;
  contactEmail: string;
  urlPath: string;
};

const defaultValuesForm = {
  name: '',
  contactName: '',
  contactEmail: '',
  urlPath: '',
};

export const CreateEditOrViewDataSourceModal = ({
  mode,
  dataSourceData,
  onCancel,
  open,
}: CreateEditOrViewDataSourceModalProps) => {
  const { t } = useTranslation();
  const { selectedCompany } = useUI();
  const { openNotification } = useFloatNotification();
  const [initialValuesOnEditForm, setInitialValuesOnEditForm] = useState({});

  const isEditing = mode === 'editing';
  const isCreating = mode === 'creating';
  const isViewing = mode === 'viewing';

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      ...defaultValuesForm,
    },
    mode: 'onChange',
  });

  const { mutateAsync: CreateDataSource, isPending: isCreatingPending } =
    useCreateDataSource();

  const { mutateAsync: UpdateDataSource, isPending: isEditingPending } =
    useUpdateDataSourceByID();

  //Logic for disabled button
  const currentValues = useWatch({ control });

  const isEditForm =
    JSON.stringify(currentValues) === JSON.stringify(initialValuesOnEditForm);

  const isNewForm =
    JSON.stringify(currentValues) === JSON.stringify(defaultValuesForm);

  const disableButton = isEditForm || isNewForm;

  const dataTableInputs: Omit<
    GenericInputProps<FormFields>,
    'control' | 'errors'
  >[] = [
    {
      inputType: InputType.INPUT,
      fieldName: 'name',
      label: t('data-sources.columns.name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('data-sources.data-source-modal.name-required'),
      },
      maxLength: NAME,
      disabled: isViewing,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'contactName',
      label: t('data-sources.columns.contact-name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('data-sources.data-source-modal.contact-name-required'),
      },
      maxLength: CONTACT_NAME,
      disabled: isViewing,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'contactEmail',
      label: t('data-sources.columns.contact-email'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('data-sources.data-source-modal.contact-email-required'),
        pattern: {
          value: EMAIL_REGEX_VALIDATION,
          message: t('data-sources.data-source-modal.error.email-validation'),
        },
      },
      maxLength: CONTACT_EMAIL,
      disabled: isViewing,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'urlPath',
      label: t('data-sources.columns.url-path'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('data-sources.data-source-modal.url-path-required'),
        pattern: {
          value: URL_REGEX_VALIDATION,
          message: t('data-sources.data-source-modal.error.url-validation'),
        },
      },
      disabled: isViewing,
    },
  ];

  useEffect(() => {
    if (dataSourceData) {
      const payload = {
        name: dataSourceData.name,
        contactName: dataSourceData.contactName,
        contactEmail: dataSourceData.contactEmail,
        urlPath: dataSourceData.urlPath,
      };
      reset(payload);
      setInitialValuesOnEditForm(payload);
    } else {
      reset(defaultValuesForm);
    }
  }, [reset, dataSourceData]);

  const text = isCreating ? (
    <DarwinTypography.Text className={styles.modalTitle}>
      {' '}
      {t('data-sources.data-source-modal.create')}{' '}
    </DarwinTypography.Text>
  ) : isEditing ? (
    <DarwinTypography.Text className={styles.modalTitle}>
      {' '}
      {t('data-sources.data-source-modal.edit')}{' '}
    </DarwinTypography.Text>
  ) : (
    <DarwinTypography.Text className={styles.modalTitle}>
      {' '}
      {t('data-sources.data-source-modal.view')}{' '}
    </DarwinTypography.Text>
  );

  const onFormSubmit = async (data: FormFields) => {
    try {
      if (isCreating) {
        await CreateDataSource({
          payload: {
            name: data.name,
            contactName: data.contactName,
            contactEmail: data.contactEmail,
            urlPath: data.urlPath,
          },
          companyId: selectedCompany || '',
        });
        openNotification(
          t('success'),
          t('data-sources.data-source-new-create-success'),
          'success'
        );
      }
      if (isEditing) {
        await UpdateDataSource({
          payload: {
            name: data.name,
            contactName: data.contactName,
            contactEmail: data.contactEmail,
            urlPath: data.urlPath,
          },
          companyId: selectedCompany || '',
          dataSourceId: dataSourceData?.id || '',
        });
        openNotification(
          t('success'),
          t('data-sources.data-source-new-update-success'),
          'success'
        );
      }
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('data-sources.data-source-new-update-error', {
            action: isCreating ? 'crear' : 'editar',
          }),
          'error'
        );
      }
    } finally {
      reset();
      onCancel();
    }
  };

  const onCancelForm = () => {
    reset();
    onCancel();
  };

  const loading = isCreatingPending || isEditingPending;

  return (
    <DarwinModal
      open={open}
      title={text}
      onCancel={onCancelForm}
      onOk={handleSubmit(onFormSubmit)}
      cancelText={t('data-sources.cancel-button')}
      okText={t('data-sources.save-button')}
      cancelButtonProps={{ className: styles.modalFooterButton }}
      okButtonProps={{
        disabled: !!disableButton,
        className: styles.modalFooterButton,
      }}
      confirmLoading={loading}
      footer={(_, { OkBtn, CancelBtn }) => (
        <DarwinFlex
          justify="center"
          align="center"
          className={styles.modalFooter}
        >
          <CancelBtn />
          <OkBtn />
        </DarwinFlex>
      )}
      centered
    >
      <Form layout="vertical" onSubmitCapture={handleSubmit(onFormSubmit)}>
        <DarwinCol>
          {dataTableInputs.map(inputProps => (
            <GenericInput<FormFields>
              key={inputProps.fieldName}
              control={control}
              errors={errors}
              {...inputProps}
            />
          ))}
        </DarwinCol>
      </Form>
    </DarwinModal>
  );
};
